/**
 * ScreenErrorBoundary Component
 *
 * React Error Boundary that catches JavaScript errors in child component tree.
 * Prevents entire app crash when a screen throws an error.
 *
 * @example
 * ```typescript
 * <ScreenErrorBoundary>
 *   <YourScreen />
 * </ScreenErrorBoundary>
 * ```
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/logger';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FadeIn, BouncyButton } from '@/components/AnimatedElements';
import { getTheme, ThemeId } from '@/styles/colors/themes';

interface ScreenErrorBoundaryProps {
  /** Child components to protect */
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ScreenErrorBoundaryState {
  /** Whether an error has been caught */
  hasError: boolean;
  /** The caught error */
  error: Error | null;
  /** Error information from React */
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that prevents screen crashes.
 *
 * Features:
 * - Catches JavaScript errors in component tree
 * - Logs errors via logger utility
 * - Provides fallback UI with retry and reload options
 * - Supports custom fallback prop
 * - Optional onError callback for error reporting
 */
export class ScreenErrorBoundary extends Component<
  ScreenErrorBoundaryProps,
  ScreenErrorBoundaryState
> {
  constructor(props: ScreenErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(
    error: Error
  ): Partial<ScreenErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error and call optional error callback
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error
    logger.error('Screen Error Boundary caught error', error, {
      component_stack: errorInfo.componentStack,
      error_boundary: 'ScreenErrorBoundary',
    });

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error boundary state
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    logger.info('Screen Error Boundary reset');
  };

  /**
   * Reload the entire page
   */
  handleReload = (): void => {
    logger.info('Screen Error Boundary triggered page reload');
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Get theme colors
      const colors = getTheme(ThemeId.DEFAULT).palette;

      // Default error UI
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4"
          style={{ backgroundColor: colors.background }}
        >
          <FadeIn direction="up" delay={0} duration={0.5}>
            <Card
              className="max-w-lg w-full shadow-xl"
              style={{ borderColor: colors.error + '40' }}
            >
              <CardHeader className="text-center pb-4">
                <div
                  className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: colors.error + '20' }}
                >
                  <svg
                    className="w-10 h-10"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: colors.error }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <CardTitle
                  className="text-2xl font-bold"
                  style={{ color: colors.text.primary }}
                >
                  Something Went Wrong
                </CardTitle>
                <CardDescription
                  className="mt-2"
                  style={{ color: colors.text.secondary }}
                >
                  We encountered an unexpected error. This has been logged and
                  we'll look into it.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Error details (only in development) */}
                {import.meta.env.DEV && this.state.error && (
                  <div
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: colors.error + '10',
                      border: `1px solid ${colors.error}40`,
                    }}
                  >
                    <p
                      className="text-sm font-semibold mb-2"
                      style={{ color: colors.error }}
                    >
                      Error Details (Development Only):
                    </p>
                    <p
                      className="text-xs font-mono break-all"
                      style={{ color: colors.error }}
                    >
                      {this.state.error.message}
                    </p>
                    {this.state.errorInfo && (
                      <details className="mt-2">
                        <summary
                          className="text-xs cursor-pointer"
                          style={{ color: colors.error }}
                        >
                          Component Stack
                        </summary>
                        <pre
                          className="text-xs mt-2 overflow-auto max-h-40"
                          style={{ color: colors.error }}
                        >
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <BouncyButton
                    onClick={this.handleReset}
                    className="flex-1 py-3 px-6 rounded-lg shadow-md transition-colors"
                    style={{
                      backgroundColor: colors.primary,
                      color: colors.text.inverse,
                    }}
                  >
                    Try Again
                  </BouncyButton>
                  <BouncyButton
                    onClick={this.handleReload}
                    className="flex-1 py-3 px-6 rounded-lg shadow-md transition-colors"
                    style={{
                      backgroundColor: colors.medium,
                      color: colors.text.inverse,
                    }}
                  >
                    Reload Page
                  </BouncyButton>
                </div>

                {/* Help text */}
                <p
                  className="text-xs text-center"
                  style={{ color: colors.text.secondary }}
                >
                  If this problem persists, please contact support.
                </p>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      );
    }

    return this.props.children;
  }
}
